import type {
  BulkOperationDto,
  CreateRiskAssessmentDto,
  GenerateDocumentLinkDto,
  RiskAssessment,
  RiskAssessmentFilters,
  UpdateRiskAssessmentDto,
} from '@nestjs-boilerplate/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiClient } from '@/lib/api-client';
import { getAuthState } from '@/stores/auth.store';

// ============================================================================
// API Response Types
// ============================================================================

export interface RiskAssessmentListResponse {
  data: RiskAssessment[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface BulkDeleteResponse {
  deleted: number;
}

export interface BulkRefreshResponse {
  refreshed: number;
  skipped: number;
}

/** Client-uploaded file metadata */
export interface UploadedDocument {
  id: string;
  documentRequestId: string;
  originalFilename: string;
  sanitizedFilename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

// ============================================================================
// API Functions
// ============================================================================

function buildQueryString(
  filters: Partial<RiskAssessmentFilters> & { page: number; pageSize: number },
): string {
  const params = new URLSearchParams();

  if (filters.search) params.set('search', filters.search);
  if (filters.riskLevels?.length) {
    for (const level of filters.riskLevels) {
      params.append('riskLevels', level);
    }
  }
  if (filters.status) params.set('status', filters.status);
  if (filters.reportDownloadWindow !== undefined) {
    params.set('reportDownloadWindow', String(filters.reportDownloadWindow));
  }
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom.toISOString());
  if (filters.dateTo) params.set('dateTo', filters.dateTo.toISOString());
  params.set('page', String(filters.page));
  params.set('pageSize', String(filters.pageSize));
  if (filters.sortBy) params.set('sortBy', filters.sortBy);
  if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);

  return params.toString();
}

export async function getRiskAssessments(
  filters: Partial<RiskAssessmentFilters> = {},
): Promise<RiskAssessmentListResponse> {
  const filtersWithDefaults = {
    page: 1,
    pageSize: 20,
    ...filters,
  };
  const queryString = buildQueryString(filtersWithDefaults);
  const endpoint = queryString ? `/risk-assessments?${queryString}` : '/risk-assessments';
  return apiClient.fetch<RiskAssessmentListResponse>(endpoint);
}

export async function getRiskAssessment(id: string): Promise<RiskAssessment> {
  return apiClient.fetch<RiskAssessment>(`/risk-assessments/${id}`);
}

export async function createRiskAssessment(data: CreateRiskAssessmentDto): Promise<RiskAssessment> {
  return apiClient.fetch<RiskAssessment>('/risk-assessments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRiskAssessment(
  id: string,
  data: UpdateRiskAssessmentDto,
): Promise<RiskAssessment> {
  return apiClient.fetch<RiskAssessment>(`/risk-assessments/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteRiskAssessment(id: string): Promise<void> {
  return apiClient.fetch<void>(`/risk-assessments/${id}`, {
    method: 'DELETE',
  });
}

export async function restoreRiskAssessment(id: string): Promise<RiskAssessment> {
  return apiClient.fetch<RiskAssessment>(`/risk-assessments/${id}/restore`, {
    method: 'POST',
  });
}

export async function refreshReport(id: string): Promise<RiskAssessment> {
  return apiClient.fetch<RiskAssessment>(`/risk-assessments/${id}/refresh-report`, {
    method: 'POST',
  });
}

export async function generateDocumentLink(
  id: string,
  data: GenerateDocumentLinkDto,
): Promise<RiskAssessment> {
  return apiClient.fetch<RiskAssessment>(`/risk-assessments/${id}/generate-document-link`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function refreshDocumentRequest(
  assessmentId: string,
  documentRequestId: string,
): Promise<void> {
  return apiClient.fetch<void>(
    `/risk-assessments/${assessmentId}/document-requests/${documentRequestId}/refresh`,
    {
      method: 'POST',
    },
  );
}

export async function bulkDeleteRiskAssessments(
  data: BulkOperationDto,
): Promise<BulkDeleteResponse> {
  return apiClient.fetch<BulkDeleteResponse>('/risk-assessments/bulk-delete', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function bulkRefreshDocuments(data: BulkOperationDto): Promise<BulkRefreshResponse> {
  return apiClient.fetch<BulkRefreshResponse>('/risk-assessments/bulk-refresh-documents', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getUploadedDocuments(assessmentId: string): Promise<UploadedDocument[]> {
  return apiClient.fetch<UploadedDocument[]>(
    `/risk-assessments/${assessmentId}/uploaded-documents`,
  );
}

/**
 * Download an uploaded document with proper authentication.
 * Fetches the file as a blob and triggers a browser download.
 */
export async function downloadUploadedDocument(
  assessmentId: string,
  fileId: string,
  filename: string,
): Promise<void> {
  const { accessToken } = getAuthState();

  const response = await fetch(
    `/api/risk-assessments/${assessmentId}/uploaded-documents/${fileId}/download`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    },
  );

  if (!response.ok) {
    throw new Error('Failed to download file');
  }

  // Get the blob and create a download link
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the blob URL
  URL.revokeObjectURL(url);
}

// ============================================================================
// Query Keys
// ============================================================================

export const riskAssessmentKeys = {
  all: ['risk-assessments'] as const,
  lists: () => [...riskAssessmentKeys.all, 'list'] as const,
  list: (filters: Partial<RiskAssessmentFilters>) =>
    [...riskAssessmentKeys.lists(), filters] as const,
  details: () => [...riskAssessmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...riskAssessmentKeys.details(), id] as const,
  uploadedDocuments: (id: string) => [...riskAssessmentKeys.all, 'uploaded-documents', id] as const,
};

// ============================================================================
// React Query Hooks
// ============================================================================

export function useRiskAssessments(filters: Partial<RiskAssessmentFilters> = {}) {
  return useQuery({
    queryKey: riskAssessmentKeys.list(filters),
    queryFn: () => getRiskAssessments(filters),
  });
}

export function useRiskAssessment(id: string) {
  return useQuery({
    queryKey: riskAssessmentKeys.detail(id),
    queryFn: () => getRiskAssessment(id),
    enabled: !!id,
  });
}

export function useCreateRiskAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRiskAssessmentDto) => createRiskAssessment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
      toast.success('Risk assessment created successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create risk assessment');
    },
  });
}

export function useUpdateRiskAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRiskAssessmentDto }) =>
      updateRiskAssessment(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.detail(id) });
      toast.success('Risk assessment updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update risk assessment');
    },
  });
}

export function useDeleteRiskAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteRiskAssessment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
      toast.success('Risk assessment deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete risk assessment');
    },
  });
}

export function useRestoreRiskAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => restoreRiskAssessment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
      toast.success('Risk assessment restored successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to restore risk assessment');
    },
  });
}

export function useRefreshReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => refreshReport(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.detail(id) });
      toast.success('Report window refreshed successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to refresh report window');
    },
  });
}

export function useGenerateDocumentLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: GenerateDocumentLinkDto }) =>
      generateDocumentLink(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.detail(id) });
      toast.success('Document request sent successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to send document request');
    },
  });
}

export function useRefreshDocumentRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      assessmentId,
      documentRequestId,
    }: {
      assessmentId: string;
      documentRequestId: string;
    }) => refreshDocumentRequest(assessmentId, documentRequestId),
    onSuccess: (_, { assessmentId }) => {
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: riskAssessmentKeys.detail(assessmentId),
      });
      toast.success('Document request resent successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to resend document request');
    },
  });
}

export function useBulkDeleteRiskAssessments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteRiskAssessments({ ids }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
      toast.success(`${response.deleted} risk assessment(s) deleted successfully`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete risk assessments');
    },
  });
}

export function useBulkRefreshDocuments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => bulkRefreshDocuments({ ids }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
      if (response.skipped > 0) {
        toast.success(
          `${response.refreshed} document request(s) refreshed, ${response.skipped} skipped (not eligible)`,
        );
      } else {
        toast.success(`${response.refreshed} document request(s) refreshed successfully`);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to refresh document requests');
    },
  });
}

export function useUploadedDocuments(assessmentId: string, enabled = true) {
  return useQuery({
    queryKey: riskAssessmentKeys.uploadedDocuments(assessmentId),
    queryFn: () => getUploadedDocuments(assessmentId),
    enabled: !!assessmentId && enabled,
  });
}
